import { findColumn, findGroup, findRow, locateRow } from "../find";
import { applyRowColumnContentRebalance } from "../column-content-min";
import { isRow } from "../sections";
import type {
  Block,
  BlockOf,
  BlockType,
  Column,
  EmailSettings,
  Group,
  Row,
} from "../types";
import type { EditorDraft } from "./types";

export function updateSettings(
  draft: EditorDraft,
  patch: Partial<EmailSettings>,
): void {
  draft.document.settings = { ...draft.document.settings, ...patch };
}

export function updateRow(
  draft: EditorDraft,
  rowId: string,
  patch: Partial<Row>,
): boolean {
  const located = locateRow(draft.document.sections, rowId);
  if (!located) {
    return false;
  }
  const next = { ...located.row, ...patch };
  if (located.location.parent === 'document') {
    const section = draft.document.sections[located.location.sectionIndex];
    if (!section || !isRow(section)) {
      return false;
    }
    draft.document.sections[located.location.sectionIndex] = next;
  } else {
    const group = findGroup(draft.document.sections, located.location.groupId);
    if (!group) {
      return false;
    }
    group.rows[located.location.rowIndex] = next;
  }
  return true;
}

export function updateGroup(
  draft: EditorDraft,
  groupId: string,
  patch: Partial<Group>,
): boolean {
  const group = findGroup(draft.document.sections, groupId);
  if (!group) {
    return false;
  }
  Object.assign(group, patch);
  return true;
}

export function updateColumn(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  patch: Partial<Column>,
): boolean {
  const row = findRow(draft.document, rowId);
  if (!row) {
    return false;
  }
  const index = row.columns.findIndex(column => column.id === columnId);
  const column = row.columns[index];
  if (!column) {
    return false;
  }
  row.columns[index] = { ...column, ...patch };
  return true;
}

export function updateBlock<T extends BlockType>(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  blockId: string,
  patch: Partial<BlockOf<T>>,
): boolean {
  const column = findColumn(draft.document, rowId, columnId);
  if (!column) {
    return false;
  }
  const index = column.blocks.findIndex(item => item.id === blockId);
  const block = column.blocks[index];
  if (!block) {
    return false;
  }
  if (patch.type !== undefined && patch.type !== block.type) {
    return false;
  }
  column.blocks[index] = { ...block, ...patch } as Block;
  applyRowColumnContentRebalance(draft.document, rowId);
  return true;
}
